import { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import OneTimeSuccess from "components/makePayment/OneTimeSuccess";
import { FailedAgreementIcon } from "icons/action/FailedIcon";
import SecureLock from "icons/payments/SecureLock";
import { CloseIcon } from "icons/payments/CloseIcon";
import {
  getTransactionDetails,
  sendReAuthPaymentEmail,
  verifyReAuthPayment,
} from "store/services/paymentServices";
import {
  parseReAuthTransactionDetails,
  type ReAuthTransactionDetails,
} from "utils/reAuthTransactionDetails";
import { useTid } from "utils/testid";
import {
  isSuccessfulEmailResponse,
  parseFailedReAuthPayment,
  parseVerifiedReAuthPayment,
  type VerifiedReAuthPayment,
} from "utils/reAuthVerification";

type ReAuthDrawerProps = {
  id: string;
  accountId?: string | number;
  clusterId?: string | number;
  leadId?: string | number;
  onClose: () => void;
};

type Phase =
  | "loading"
  | "ready"
  | "authenticating"
  | "verifying"
  | "sending-receipt"
  | "verification-error"
  | "load-error"
  | "failed"
  | "success";

const formatUsd = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

const ReAuthDrawer = ({
  id,
  accountId,
  clusterId,
  leadId,
  onClose,
}: ReAuthDrawerProps) => {
  const tid = useTid();
  const [phase, setPhase] = useState<Phase>("loading");
  const [details, setDetails] = useState<ReAuthTransactionDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const [reloadCount, setReloadCount] = useState(0);
  const [emailSent, setEmailSent] = useState(true);
  const [verifiedReceipt, setVerifiedReceipt] =
    useState<VerifiedReAuthPayment | null>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  const submittingRef = useRef(false);
  const verifyingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const sendReceipt = useCallback(async (orderId: number, emails: string[]) => {
    const response = await sendReAuthPaymentEmail({
      invoice_id: orderId,
      ...(accountIdRef.current ? { accountid: accountIdRef.current } : {}),
      emails,
      shouldSendAll: false,
    });
    if (!isSuccessfulEmailResponse(response)) {
      throw new Error("Receipt email was not sent");
    }
  }, []);

  const verifyAndFinish = useCallback(async (transactionId: string) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setErrorMessage("");
    setPhase("verifying");

    try {
      const response = await verifyReAuthPayment({
        paymentslug: id,
        payment_intent_id: transactionId,
      });
      const failed = parseFailedReAuthPayment(response, transactionId);
      if (failed) {
        if (mountedRef.current) {
          setFailureMessage(failed.reason || "Your card authentication was unsuccessful. This payment was not completed.");
          setPhase("failed");
        }
        return;
      }
      const verified = parseVerifiedReAuthPayment(response, transactionId);
      if (!verified) {
        if (mountedRef.current) {
          setErrorMessage("We couldn't confirm your payment yet. Check its status again in a moment.");
          setPhase("verification-error");
        }
        return;
      }

      if (mountedRef.current) {
        setVerifiedReceipt(verified);
        setPhase("sending-receipt");
      }
      let receiptSent = false;
      try {
        await sendReceipt(verified.orderId, [verified.email]);
        receiptSent = true;
      } catch {
        // A receipt failure must not turn a verified payment into a failed payment.
      }

      if (mountedRef.current) {
        setEmailSent(receiptSent);
        setPhase("success");
      }
    } catch (error) {
      if (mountedRef.current) {
        const serverResponse = (error as { response?: { data?: unknown } })?.response?.data;
        const failed = parseFailedReAuthPayment(serverResponse, transactionId);
        if (failed) {
          setFailureMessage(failed.reason || "Your card authentication was unsuccessful. This payment was not completed.");
          setPhase("failed");
        } else {
          setErrorMessage("We couldn't confirm your payment yet. Check its status again in a moment.");
          setPhase("verification-error");
        }
      }
    } finally {
      verifyingRef.current = false;
    }
  }, [id, sendReceipt]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setPhase("loading");
    setErrorMessage("");
    setFailureMessage("");
    setDetails(null);
    setEmailSent(true);
    setVerifiedReceipt(null);
    stripeRef.current = null;
    submittingRef.current = false;

    const loadDetails = async () => {
      try {
        const response = await getTransactionDetails(id, controller.signal);
        const transaction = parseReAuthTransactionDetails(response);
        if (!transaction) {
          throw new Error("Incomplete transaction details");
        }

        const stripe = await loadStripe(transaction.publishableKey);
        if (!stripe) {
          throw new Error("Stripe could not be initialized");
        }

        const { error: intentError, paymentIntent } =
          await stripe.retrievePaymentIntent(transaction.clientSecret);
        if (intentError || !paymentIntent) {
          throw new Error("PaymentIntent could not be retrieved");
        }
        if (
          paymentIntent.id !== transaction.transactionId ||
          paymentIntent.currency.toLowerCase() !== "usd" ||
          paymentIntent.amount !== Math.round(transaction.totalAmount * 100)
        ) {
          throw new Error("Payment total mismatch");
        }

        if (active) {
          setDetails(transaction);
          if (
            paymentIntent.status === "succeeded" ||
            paymentIntent.status === "processing" ||
            paymentIntent.status === "requires_capture" ||
            paymentIntent.status === "canceled" ||
            paymentIntent.status === "requires_payment_method"
          ) {
            void verifyAndFinish(transaction.transactionId);
            return;
          }
          stripeRef.current = stripe;
          setPhase("ready");
        }
      } catch (error) {
        if (active && !controller.signal.aborted) {
          setErrorMessage(
            error instanceof Error && error.message === "Payment total mismatch"
              ? "We couldn't verify the payment total. Please contact support."
              : "We couldn't load this payment. Please try again."
          );
          setPhase("load-error");
        }
      }
    };

    loadDetails();
    return () => {
      active = false;
      controller.abort();
    };
  }, [id, reloadCount, verifyAndFinish]);

  const authenticate = async () => {
    const stripe = stripeRef.current;
    if (!stripe || !details || submittingRef.current) return;

    submittingRef.current = true;
    setErrorMessage("");
    setPhase("authenticating");

    try {
      // The PaymentIntent already has the saved card. Its client secret lets
      // Stripe.js display any required 3DS challenge without card entry.
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        details.clientSecret
      );

      if (error) {
        await verifyAndFinish(details.transactionId);
      } else if (paymentIntent?.id !== details.transactionId) {
        setErrorMessage("We couldn't confirm this payment. Please try again.");
        setPhase("ready");
      } else if (
        paymentIntent.status === "succeeded" ||
        paymentIntent.status === "requires_confirmation" ||
        paymentIntent.status === "requires_capture" ||
        paymentIntent.status === "processing" ||
        paymentIntent.status === "canceled" ||
        paymentIntent.status === "requires_payment_method"
      ) {
        await verifyAndFinish(paymentIntent.id);
      } else {
        setErrorMessage("Verification wasn't completed. Please try again.");
        setPhase("ready");
      }
    } catch {
      setErrorMessage("Verification couldn't be started. Please try again.");
      setPhase("ready");
    } finally {
      submittingRef.current = false;
    }
  };

  const sendAdditionalReceipt = async ({ emails }: { emails: unknown }) => {
    const recipient = Array.isArray(emails) && typeof emails[0] === "string"
      ? emails[0].trim()
      : "";
    if (!verifiedReceipt || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      throw new Error("A valid receipt email is required");
    }
    await sendReceipt(verifiedReceipt.orderId, [recipient]);
  };

  if (phase === "failed") {
    return (
      <div className="flex h-full flex-col bg-bgPrimary text-txtPrimary" data-testid={tid("re-auth-failed")}>
        <header className="flex justify-end px-5 py-5 lg:px-10">
          <button
            type="button"
            aria-label="Close payment reauthentication"
            className="rounded-full p-2 hover:!bg-bgSecondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={onClose}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </header>
        <main className="flex flex-1 flex-col items-center justify-center gap-8 px-5 pb-16 text-center lg:px-10">
          <FailedAgreementIcon aria-hidden="true" />
          <div className="flex flex-col items-center gap-4">
            <h2 className="text-2xl font-medium">Payment reauthenticate failed</h2>
            <p className="max-w-[500px] text-base leading-6 text-txtParagraph">
              {failureMessage}
            </p>
          </div>
          <button
            type="button"
            className="text-btnPrimaryActive hover:!text-btnPrimaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={onClose}
          >
            Back to payments
          </button>
        </main>
      </div>
    );
  }

  if (phase === "success" && details && verifiedReceipt) {
    return (
      <div className="h-full overflow-y-auto bg-bgPrimary flex items-center justify-center" data-testid={tid("re-auth-success")}>
        <OneTimeSuccess
          title="Payment reauthentication completed"
          email={verifiedReceipt.email}
          amount={details.totalAmount}
          invoiceId={verifiedReceipt.orderId}
          sendEmail={sendAdditionalReceipt}
          clusterId={clusterId || ""}
          lead_id={leadId}
          receiptEmailSent={emailSent}
          showSuccessToast
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col bg-bgPrimary text-txtPrimary"
      data-testid={tid("re-auth-drawer")}
    >
      <header className="flex items-center justify-between gap-4 border-b border-strokePrimary px-5 py-5 lg:px-10">
        <h2 className="text-lg font-medium leading-[30px] lg:text-[26px]">
          Payment method reauthentication
        </h2>
        <button
          type="button"
          aria-label="Close payment method reauthentication"
          className="shrink-0 cursor-pointer rounded-full p-2 hover:!bg-bgSecondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={onClose}
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </header>

      <main className="flex flex-1 flex-col overflow-y-auto px-5 py-10 lg:px-10">
        {phase === "loading" || phase === "verifying" || phase === "sending-receipt" ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4 text-center"
            role="status"
            data-testid={tid(phase === "loading" ? "re-auth-loading" : "re-auth-verifying")}
          >
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent motion-reduce:animate-none" />
            <p className="text-base text-txtParagraph">
              {phase === "loading"
                ? "Loading payment details…"
                : phase === "verifying"
                  ? "Confirming your payment…"
                  : "Sending your receipt…"}
            </p>
          </div>
        ) : phase === "verification-error" ? (
          <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col justify-center gap-5">
            <h3 className="text-2xl font-medium">Payment confirmation pending</h3>
            <p role="alert" className="text-base text-txtParagraph">
              {errorMessage}
            </p>
            <button
              type="button"
              className="w-full rounded-md bg-btnPrimaryActive px-4 py-3 font-medium text-white hover:bg-btnPrimaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => details && void verifyAndFinish(details.transactionId)}
            >
              Check payment status
            </button>
          </div>
        ) : phase === "load-error" ? (
          <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col justify-center gap-5">
            <h3 className="text-2xl font-medium">Payment details unavailable</h3>
            <p role="alert" className="text-base text-txtParagraph">
              {errorMessage}
            </p>
            <button
              type="button"
              className="w-full rounded-md bg-btnPrimaryActive px-4 py-3 font-medium text-white hover:bg-btnPrimaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => setReloadCount((count) => count + 1)}
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col justify-center">
            <h3 className="text-2xl font-medium leading-8">Complete your payment</h3>
            <p className="mt-4 text-base leading-6 text-txtParagraph">
              Your card issuer needs to authenticate this payment. After you approve,
              your card will be charged the total shown below. Your bank may ask for
              a one-time code or approval in its app.
            </p>
            {details && (
              <section
                aria-label="Payment summary"
                className="mt-8 rounded-md border border-strokePrimary bg-white px-5 py-5"
                data-testid={tid("re-auth-payment-summary")}
              >
                <h4 className="text-base font-medium">Payment summary</h4>
                <dl className="mt-4 flex flex-col gap-3 text-sm text-txtParagraph">
                  <div className="flex items-center justify-between gap-4">
                    <dt>Amount</dt>
                    <dd>{formatUsd(details.amount)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt>Card fee</dt>
                    <dd>{formatUsd(details.feeAmount)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4 border-t border-strokePrimary pt-3 text-base font-medium text-txtPrimary">
                    <dt>Total to pay</dt>
                    <dd>{formatUsd(details.totalAmount)}</dd>
                  </div>
                </dl>
              </section>
            )}
            {errorMessage && (
              <p role="alert" className="mt-5 text-sm text-red-600">
                {errorMessage}
              </p>
            )}
            <button
              type="button"
              disabled={phase === "authenticating"}
              aria-busy={phase === "authenticating"}
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-md bg-btnPrimaryActive px-4 py-3 font-medium text-white hover:bg-btnPrimaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-60"
              onClick={authenticate}
              data-testid={tid("re-auth-continue")}
            >
              {phase === "authenticating" && (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-r-transparent motion-reduce:animate-none" aria-hidden="true" />
              )}
              {phase === "authenticating"
                ? "Authenticating and processing…"
                : details
                  ? `Authenticate and pay ${formatUsd(details.totalAmount)}`
                  : "Authenticate and pay"}
            </button>
            <p className="mt-4 text-center text-sm leading-5 text-txtParagraph">
              Authentication takes place securely with your card issuer.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReAuthDrawer;
